import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { FileRejection } from "react-dropzone";

import { selectFileFromDropBox } from "libs/dropbox";
import { selectFileFromCanva } from "libs/canva";
import { selectFileFromGoogleDrive } from "libs/google-drive";
import { getFileTypeByName, loadBlob } from "libs/files";
import { openModal } from "libs/modals";
import { FileToUpload } from "api/media-library";
import { ADD_FRONTIFY_MODAL } from "libs/frontify";
import { getUser } from "selectors/commonSelectors";
import { FRONTIFY_WIDGET_CLICKED } from "constants/Events";
import { useTrackEvent } from "libs/analytics";

interface UseFileSelectionHandlersParams {
  onSelect: (files: FileToUpload[]) => void;
  limit: number;
  isDisabled: boolean;
}

interface UseFileSelectionHandlersReturn {
  // Handlers
  onDrop: (
    acceptedFiles: File[],
    fileRejections: FileRejection[]
  ) => Promise<void>;
  handleDropBoxClick: () => Promise<void>;
  handleGoogleDriveClick: () => Promise<void>;
  handleCanvaTypeClick: (type: string) => Promise<void>;
  handleCanvaClick: () => void;
  handleFrontifyClick: () => void;

  // State
  isCanva: boolean;

  // Utilities
  dropzoneConfig: {
    accept: {
      "image/*": string[];
      "video/*": string[];
      "application/pdf": string[];
    };
    onError: (error: any) => void;
    noClick: boolean;
    disabled: boolean;
  };
}

export const useFileSelectionHandlers = ({
  onSelect,
  limit,
  isDisabled,
}: UseFileSelectionHandlersParams): UseFileSelectionHandlersReturn => {
  const dispatch = useDispatch();
  const trackEvent = useTrackEvent();
  const user = useSelector(getUser);
  const [isCanva, setIsCanva] = useState(false);

  const onDrop = async (
    acceptedFiles: File[],
    fileRejections: FileRejection[]
  ) => {
    try {
      for (const { file, errors } of fileRejections) {
        toast.error(
          `${file.name} was rejected: ${errors
            .map((e) => e.message)
            .join(", ")}`
        );
      }

      const files = await Promise.all(
        acceptedFiles
          .slice(0, limit)
          .map(async (file): Promise<FileToUpload> => {
            // TODO: 'file' is actually already a Blob, so we most probably
            // don't need this loadBlob call moreover loadBlob currently
            // does not do any useful work
            const blob = await loadBlob(file);
            return {
              contentType: "blob",
              name: file.name,
              blob,
              type: getFileTypeByName(file.name),
            };
          })
      );

      onSelect(files);
    } catch (error: any) {
      toast.error(error && error.message ? error.message : error);
    }
  };

  const handleDropBoxClick = async () => {
    try {
      setIsCanva(false);
      const dropBoxFiles = await selectFileFromDropBox();
      const files = dropBoxFiles.slice(0, limit).map(
        (dropBoxFile): FileToUpload => ({
          contentType: "url",
          name: dropBoxFile.name,
          url: dropBoxFile.link,
          thumbnail: dropBoxFile.thumbnailLink,
          type: getFileTypeByName(dropBoxFile.name),
        })
      );
      onSelect(files);
    } catch (error: any) {
      toast.error(error && error.message ? error.message : error);
    }
  };

  const handleGoogleDriveClick = async () => {
    try {
      setIsCanva(false);
      const gDriveFiles = await selectFileFromGoogleDrive();
      const files = gDriveFiles.map(
        (file): FileToUpload => ({
          contentType: "getter",
          name: file.name,
          getImage: file.getImage,
          type: getFileTypeByName(file.name),
        })
      );

      onSelect(files);
    } catch (error: any) {
      toast.error(error && error.message ? error.message : error);
    }
  };

  const handleCanvaTypeClick = async (type: string) => {
    try {
      const canvaFile = await selectFileFromCanva(type);
      onSelect([
        {
          contentType: "url",
          name: `${canvaFile.designTitle}.jpg`,
          url: canvaFile.exportUrl,
          thumbnail: canvaFile.exportUrl,
          // Canva button embed only currently supports
          // exporting image formats. TODO when it supports video/gif, support here
          type: "image",
        } as FileToUpload,
      ]);
    } catch (error: any) {
      toast.error(error && error.message ? error.message : error);
    }
  };

  const handleCanvaClick = () => {
    setIsCanva(!isCanva);
  };

  const handleFrontifyClick = () => {
    trackEvent({
      eventName: FRONTIFY_WIDGET_CLICKED,
      useServices: ["AMP", "AC"],
    });
    try {
      setIsCanva(false);
      dispatch(
        openModal({
          type: ADD_FRONTIFY_MODAL,
          props: {
            limit: limit,
            onSelect: onSelect,
          },
        })
      );
    } catch (error: any) {
      toast.error(error?.message || "An error occurred");
    }
  };

  const dropzoneConfig = {
    accept: {
      "image/*": [
        ".jpg",
        ".jpeg",
        ".png",
        ".gif",
        ".webp",
        ".heic",
        ".heif",
        ".bmp",
        ".tif",
        ".tiff",
      ],
      "video/*": [".mp4", ".mov", ".avi", ".webm"],
      "application/pdf": [".pdf"],
    },
    onError: (error: any) => {
      console.error(error);
      toast.error(error?.message || "An error occurred");
    },
    noClick: true,
    disabled: isDisabled,
  };

  return {
    onDrop,
    handleDropBoxClick,
    handleGoogleDriveClick,
    handleCanvaTypeClick,
    handleCanvaClick,
    handleFrontifyClick,
    isCanva,
    dropzoneConfig,
  };
};
